import React from 'react';
import { Paperclip, X, FileText, Image as ImageIcon, Eye } from 'lucide-react';
import { Caption } from './Typography';

export function FileUpload({ 
  files = [], 
  onFileChange, 
  onRemove, 
  onPreview, 
  accept = "image/*,.pdf", 
  multiple = true,
  maxFiles = 25,
  label = "Upload Files",
  helperText = "JPG, PNG or PDF (Max 100MB per file)",
  className = ""
}) {
  const fileInputRef = React.useRef(null);

  const handleContainerClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <div className="flex items-center justify-between pl-1">
        <Caption className="font-bold">{label}</Caption>
        {maxFiles && (
          <span className="text-[10px] font-bold text-brand-blue bg-blue-50 px-2.5 py-1 rounded-full border border-blue-100 uppercase tracking-tight">
            MAX {maxFiles} FILES
          </span>
        )}
      </div>
      
      <div
        onClick={handleContainerClick}
        className="border-2 border-dashed border-slate-100 rounded-2xl p-10 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-brand-blue/30 hover:bg-brand-blue/5 transition-all text-slate-400 group bg-slate-50/50"
      >
        <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center text-slate-400 group-hover:text-brand-blue transition-colors border border-slate-50">
          <Paperclip size={24} />
        </div>
        <div className="text-center">
          <div className="text-[13px] font-bold text-slate-600">Click to upload or drag & drop</div>
          <div className="text-[10px] font-medium text-slate-400 mt-0.5">{helperText}</div>
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileChange}
        multiple={multiple}
        accept={accept}
        className="hidden"
      />

      {files.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {files.map((file, idx) => (
            <div key={idx} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100 group hover:border-brand-blue/30 hover:shadow-md transition-all animate-in zoom-in-95 duration-200">
              <div className="p-2 bg-slate-50 rounded-lg text-brand-blue border border-slate-100">
                {file.type === 'application/pdf' ? <FileText size={18} /> : <ImageIcon size={18} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold text-brand-dark truncate">{file.name}</div>
                <div className="text-[10px] text-slate-400 font-bold">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {onPreview && (
                  <button
                    type="button"
                    onClick={() => onPreview(file)}
                    className="p-1.5 text-slate-400 hover:text-brand-blue hover:bg-brand-blue/10 rounded-lg transition-colors"
                  >
                    <Eye size={14} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
